import { create } from 'zustand';
import { apiService } from '../services/api';
import { getErrorMessage } from '../services/errorHandler';

interface DriverStatus {
  is_online: boolean;
  driver_status: 'OFFLINE' | 'ONLINE' | 'BUSY' | 'ON_RIDE';
  verification_status: 'pending' | 'approved' | 'rejected';
  can_accept_rides: boolean;
  documents_approved?: number;
  required_documents?: Array<{ type: string; name: string }>;
  hasVehicle?: boolean;
  hasAllRequiredDocuments?: boolean;
}

interface Vehicle {
  id: string;
  vehicle_type_id: string;
  vehicle_type_ids?: number[];
  plate_number: string;
  model: string;
  year: number;
  color: string;
  seat_count: number;
  is_active: boolean;
}

interface DriverDocument {
  id: string;
  document_type: string;
  document_number: string;
  verification_status: string;
  uploaded_at: string;
  verified_at?: string;
  rejection_reason?: string;
}

interface DriverState {
  status: DriverStatus | null;
  vehicles: Vehicle[];
  documents: DriverDocument[];
  earnings: any;
  stats: any;
  isLoading: boolean;
  setOnline: (online: boolean) => Promise<void>;
  updateLocation: (latitude: number, longitude: number) => Promise<void>;
  getVehicles: () => Promise<void>;
  addVehicle: (vehicleData: any) => Promise<void>;
  updateVehicle: (vehicleId: string, vehicleData: any) => Promise<void>;
  deleteVehicle: (vehicleId: string) => Promise<void>;
  getDocuments: () => Promise<void>;
  uploadDocument: (file: File, documentType: string, documentNumber: string, expiryDate?: string) => Promise<void>;
  getVerificationStatus: () => Promise<void>;
  getEarnings: () => Promise<void>;
  getStats: () => Promise<void>;
}

export const useDriverStore = create<DriverState>((set, get) => ({
  status: null,
  vehicles: [],
  documents: [],
  earnings: null,
  stats: null,
  isLoading: false,

  setOnline: async (online: boolean) => {
    try {
      console.log('🔄 Setting online status to:', online);
      const response: any = await apiService.post<DriverStatus>('/drivers/status', { is_online: online });
      console.log('✅ Status update response:', response);


      await get().getVerificationStatus();


      const currentStatus = get().status;
      if (currentStatus) {
        set({
          status: {
            ...currentStatus,
            is_online: online,
            driver_status: online ? 'ONLINE' : 'OFFLINE',
            can_accept_rides: response.can_accept_rides ?? currentStatus.can_accept_rides,
          },
        });
      }
    } catch (error: any) {
      console.error('❌ Failed to update status:', error);
      throw new Error(getErrorMessage(error) || 'Failed to update status');
    }
  },

  updateLocation: async (latitude: number, longitude: number) => {
    try {
      await apiService.post('/drivers/location', {
        latitude,
        longitude,
      });
    } catch (error) {
      console.error('Failed to update location:', error);
    }
  },

  getVehicles: async () => {
    set({ isLoading: true });
    try {
      const vehicles: any = await apiService.get<{ data: Vehicle[] }>('/drivers/vehicles');
      set({ vehicles: vehicles.data || [] });
    } catch (error) {
      console.error('Failed to get vehicles:', error);
    } finally {
      set({ isLoading: false });
    }
  },

  addVehicle: async (vehicleData: any) => {
    try {

      const { vehicle_type_ids, ...cleanData } = vehicleData;


      const payload: any = {
        vehicle_type_id: Number(cleanData.vehicle_type_id),
        plate_number: String(cleanData.plate_number || '').trim(),
        model: String(cleanData.model || '').trim(),
        year: Number(cleanData.year),
        color: String(cleanData.color || '').trim(),
      };


      if (cleanData.seat_count !== undefined) {
        payload.seat_count = Number(cleanData.seat_count);
      }


      if (!payload.vehicle_type_id || payload.vehicle_type_id <= 0) {
        throw new Error('Vehicle type is required');
      }
      if (!payload.plate_number) {
        throw new Error('Plate number is required');
      }
      if (!payload.model) {
        throw new Error('Model is required');
      }
      if (!payload.year || payload.year < 1900 || payload.year > new Date().getFullYear() + 1) {
        throw new Error('Valid year is required');
      }
      if (!payload.color) {
        throw new Error('Color is required');
      }

      if (import.meta.env.DEV) {
        console.log('📤 Adding vehicle with data:', payload);
      }

      await apiService.post('/drivers/vehicles', payload);
      await get().getVehicles();
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error('❌ Failed to add vehicle:', error);
      }
      throw new Error(error.message || 'Failed to add vehicle');
    }
  },

  updateVehicle: async (vehicleId: string, vehicleData: any) => {
    try {

      const { vehicle_type_ids, ...cleanData } = vehicleData;
      await apiService.put(`/drivers/vehicles/${vehicleId}`, cleanData);
      await get().getVehicles();
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update vehicle');
    }
  },

  deleteVehicle: async (vehicleId: string) => {
    try {
      await apiService.delete(`/drivers/vehicles/${vehicleId}`);
      await get().getVehicles();
    } catch (error: any) {
      throw new Error(error.message || 'Failed to delete vehicle');
    }
  },

  getDocuments: async () => {
    set({ isLoading: true });
    try {
      const documents: any = await apiService.get<DriverDocument[]>('/auth/driver/documents');
      set({ documents });
    } catch (error) {
      console.error('Failed to get documents:', error);
    } finally {
      set({ isLoading: false });
    }
  },

  uploadDocument: async (
    file: File,
    documentType: string,
    documentNumber: string,
    expiryDate?: string,
  ) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);
      formData.append('document_number', documentNumber);
      if (expiryDate) {
        formData.append('expiry_date', expiryDate);
      }

      await apiService.upload('/auth/driver/documents/upload', formData);
      await get().getDocuments();

      await get().getVerificationStatus();
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to upload document';
      throw new Error(errorMessage);
    }
  },

  getVerificationStatus: async () => {
    try {

      const status: any = await apiService.get<DriverStatus>('/auth/driver/verification/status');
      console.log('📊 Verification status received:', JSON.stringify(status, null, 2));


      const normalizedStatus = {
        ...status,
        verification_status: (status.verification_status || '').toLowerCase() as any,
      };

      console.log('🔍 Current verification status:', {
        raw: status.verification_status,
        normalized: normalizedStatus.verification_status,
        isVerified: normalizedStatus.verification_status === 'approved',
        isPending: normalizedStatus.verification_status === 'pending',
        isRejected: normalizedStatus.verification_status === 'rejected',
        canAcceptRides: status.can_accept_rides,
        documentsApproved: status.documents_approved,
        requiredDocuments: status.required_documents?.length || 0,
        hasVehicle: (status as any).hasVehicle || false,
        hasAllRequiredDocuments: (status as any).hasAllRequiredDocuments || false,
      });


      if (normalizedStatus.verification_status === 'approved' && !status.can_accept_rides) {
        console.warn('⚠️ WARNING: Verification is approved but can_accept_rides is false!', {
          documentsApproved: status.documents_approved,
          requiredDocuments: status.required_documents?.length || 0,
          hasVehicle: (status as any).hasVehicle,
          hasAllRequiredDocuments: (status as any).hasAllRequiredDocuments,
        });
      }

      set({ status: normalizedStatus });
    } catch (error) {
      console.error('❌ Failed to get verification status:', error);
    }
  },

  getEarnings: async () => {
    try {
      const earnings: any = await apiService.get('/drivers/earnings');
      set({ earnings });
    } catch (error) {
      console.error('Failed to get earnings:', error);
    }
  },

  getStats: async () => {
    try {
      const stats: any = await apiService.get('/drivers/stats');
      set({ stats });
    } catch (error) {
      console.error('Failed to get stats:', error);
    }
  },
}));

